<template>
  <img
    :src="logoSrc"
    :alt="alt"
    :class="['logo', sizeClass]"
  />
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'

// Import logo assets
import logoLight from '@/assets/logo-light-mode.svg'
import logoDark from '@/assets/logo-dark-mode.svg'
import logoIcon from '@/assets/logo-icon.svg'

interface Props {
  variant?: 'full' | 'icon'
  theme?: 'light' | 'dark' | 'auto'
  size?: 'sm' | 'md' | 'lg'
  alt?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'full',
  theme: 'auto',
  size: 'md',
  alt: 'StructEngine'
})

// Detect current theme from document root
const currentTheme = ref<'light' | 'dark'>('dark')
let observer: MutationObserver | null = null

const detectTheme = () => {
  const root = document.documentElement
  const hasLightClass = root.classList.contains('theme-light')
  currentTheme.value = hasLightClass ? 'light' : 'dark'
  if (import.meta.env.DEV) console.log('[Logo] Theme detected:', currentTheme.value, 'hasLightClass:', hasLightClass)
}

onMounted(() => {
  // Initial detection
  detectTheme()

  // Create per-component observer (not shared on window)
  observer = new MutationObserver(() => {
    detectTheme()
  })

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class']
  })
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
    observer = null
  }
})

const logoSrc = computed(() => {
  if (props.variant === 'icon') return logoIcon

  // If theme is explicitly set, use it
  if (props.theme === 'light') return logoLight
  if (props.theme === 'dark') return logoDark

  // Auto mode: detect from theme class
  // Light theme (light background) needs light-mode logo (with dark text)
  // Dark theme (dark background) needs dark-mode logo (with white text)
  return currentTheme.value === 'light' ? logoLight : logoDark
})

const sizeClass = computed(() => `logo--${props.size}`)
</script>

<style scoped>
.logo {
  display: block;
  height: auto;
  max-width: 100%;
  /* Remove any inherited padding/margin */
  margin: 0;
  padding: 0;
}
.logo--sm { height: 80px; }
.logo--md { height: 120px; }
.logo--lg { height: 160px; }
</style>
